import React, { createContext, useContext, useState } from 'react';

// Creating the context with default values for safety
const ErrorContext = createContext({
    error: null,
    showError: () => {},
    hideError: () => {}
});


/**
 * Custom hook to access the error context
 * 
 * @returns {Object} The error context including error, errorMessage, showError, and hideError.
 * @throws Will throw an error if the hook is used outside of an ErrorProvider.
 * 
 * @category Context
 */
export const useError = () => {
    const context = useContext(ErrorContext);
    if (!context) {
        throw new Error('useError must be used within an ErrorProvider');
    }
    return context;
};

/**
 * Provides app access to error context
 * 
 * @param {React.ReactNode} props.children - children components to be wrapped by the provider
 * 
 * @returns {JSX.Element}
 * 
 * @category Context
 */
export const ErrorProvider = ({ children }) => {
    const [error, setError] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');

    const showError = (errorMsg) => {
        setError(true);
        setErrorMessage(errorMsg);
    };

    const hideError = () => {
        setError(null);
        setErrorMessage('');
    };

    const contextValue = {
        error,
        errorMessage,
        showError,
        hideError
    };

    return (
        <ErrorContext.Provider value={contextValue}>
            {children}
        </ErrorContext.Provider>
    );
};
